import type { NostrEvent } from '@nostrify/nostrify';
import { Card, CardContent } from '@/components/ui/card';
import {
  extractMusicTitle,
  extractMusicArtist,
  extractMusicUrl,
  extractMusicImage,
  extractMusicDuration,
} from '@/hooks/useMusic';
import { Music, Pause, Play } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';

interface MusicCardProps {
  event: NostrEvent;
}

export function MusicCard({ event }: MusicCardProps) {
  const title = extractMusicTitle(event);
  const artist = extractMusicArtist(event);
  const audioUrl = extractMusicUrl(event);
  const imageUrl = extractMusicImage(event);
  const duration = extractMusicDuration(event);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!audioUrl) return null;

  return (
    <Card className="hover:shadow-lg transition-shadow duration-300">
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-shrink-0">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={title || 'Album artwork'}
                className="w-16 h-16 rounded-lg object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center">
                <Music className="h-8 w-8 text-muted-foreground" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm truncate">{title || 'Untitled'}</h3>
            <p className="text-xs text-muted-foreground truncate">
              {artist || 'Unknown Artist'}
            </p>
            {duration !== null && (
              <p className="text-xs text-muted-foreground mt-1">
                {isPlaying ? formatTime(currentTime) : formatTime(0)} / {formatTime(duration)}
              </p>
            )}
          </div>

          <Button
            variant="ghost"
            size="icon"
            onClick={togglePlay}
            className="flex-shrink-0"
          >
            {isPlaying ? (
              <Pause className="h-5 w-5" />
            ) : (
              <Play className="h-5 w-5" />
            )}
          </Button>
        </div>

        <audio ref={audioRef} src={audioUrl} preload="metadata" />
      </CardContent>
    </Card>
  );
}
